/**
 * PipelinePayoutTargets — per-sheet re-runs for the payoutSheets stage (eight
 * targets + re-run all), rendered inside the stage row. Targeted re-runs merge
 * per key into the stage result and never blank sibling rows. Ported verbatim
 * from PipelineRerunPanel (targetStatus encodes the channelSheetSummary
 * error-count semantics and the targetRuns stamps).
 */

import React from 'react';
import { Button, StatusPill } from '../../ui';
import type { StatusVariant } from '../../ui';
import { PipelineRun, PayoutTarget } from '../../../services/adminApi';

interface PipelinePayoutTargetsProps {
  pipeline: PipelineRun;
  /** cmLock confirmed and no payout run in flight */
  enabled: boolean;
  actionBusy: string | null;
  onGenerate: (targets?: PayoutTarget[]) => Promise<void>;
}

/** Maps each payout target to its key in the payoutSheets stage result. */
const PAYOUT_TARGETS: Array<{
  target: PayoutTarget;
  label: string;
  resultKey: 'channelSheetSummary' | 'adSense' | 'directSold' | 'wassermanSheet' | 'teamSheet' | 'ericaSheet' | 'nightcoSheet' | 'skaraSheet';
}> = [
  { target: 'adSense', label: 'Google AdSense [CONSOLIDATED]', resultKey: 'adSense' },
  { target: 'directSold', label: 'Direct Sold [CONSOLIDATED]', resultKey: 'directSold' },
  { target: 'trackerSummaries', label: 'Tracker summaries + legacy XLSX mirror', resultKey: 'channelSheetSummary' },
  { target: 'wasserman', label: 'Wasserman', resultKey: 'wassermanSheet' },
  { target: 'team', label: 'The Team', resultKey: 'teamSheet' },
  { target: 'erica', label: 'Erica Heyward', resultKey: 'ericaSheet' },
  { target: 'nightco', label: 'Night Co', resultKey: 'nightcoSheet' },
  { target: 'skara', label: 'Skara Payments Tracker', resultKey: 'skaraSheet' },
];

const fmtUsd = (n: unknown): string =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';

/**
 * Human label for a payout-row pill. StatusPill renders its `variant` verbatim
 * when given no children, so every state needs an explicit label.
 */
const pillLabel = (pill: StatusVariant): string => {
  switch (pill) {
    case 'live': return 'running';
    case 'flagged': return 'failed';
    case 'draft': return 'pending';
    case 'ok': return 'done';
    default: return pill;
  }
};

const PipelinePayoutTargets: React.FC<PipelinePayoutTargetsProps> = ({
  pipeline,
  enabled,
  actionBusy,
  onGenerate,
}) => {
  const payoutStage = pipeline.stages.payoutSheets;
  const activeTargets = new Set(payoutStage.activeTargets || []);
  const payoutResult = (payoutStage.result as any) || {};
  const targetRuns = payoutResult.targetRuns || {};

  const targetStatus = (resultKey: string, target: PayoutTarget): { pill: StatusVariant; note: string } => {
    const stamp = targetRuns[target];
    const value = payoutResult[resultKey];
    if (activeTargets.has(target) && payoutStage.status === 'running') {
      return { pill: 'live', note: 'Regenerating…' };
    }
    if (stamp && stamp.success === false) {
      return { pill: 'flagged', note: `Failed ${new Date(stamp.at).toLocaleString()}` };
    }
    if (!value) return { pill: 'draft', note: 'Not generated yet' };
    // trackerSummaries (channelSheetSummary) has NO `success` field — its outcome is the
    // per-channel error arrays, which resolve normally (updateChannelSheetSummaries never
    // throws on a single-channel failure). Surface any errors as a failure so the pill
    // doesn't sit green while the summary text reports errors.
    const errorCount = resultKey === 'channelSheetSummary'
      ? (value.errors?.length ?? 0) + (value.mirrorErrors?.length ?? 0)
      : 0;
    const failed = resultKey === 'channelSheetSummary' ? errorCount > 0 : value.success === false;
    if (failed) return { pill: 'flagged', note: value.error || (errorCount ? `${errorCount} error(s)` : 'Failed') };
    const at = stamp?.at || payoutStage.completedAt;
    return { pill: 'ok', note: at ? `Generated ${new Date(at).toLocaleString()}` : 'Generated' };
  };

  const targetSummary = (resultKey: string): { text: string; url?: string } => {
    const v = payoutResult[resultKey];
    if (!v) return { text: '' };
    switch (resultKey) {
      case 'adSense':
      case 'directSold':
      case 'wassermanSheet':
      case 'teamSheet':
        return {
          text: [v.tabName, typeof v.channelCount === 'number' ? `${v.channelCount} channels` : '', fmtUsd(v.grandTotal)]
            .filter(Boolean)
            .join(' · '),
          url: v.sheetUrl,
        };
      case 'ericaSheet':
      case 'nightcoSheet':
        return {
          text: [v.directSalesTab, fmtUsd(v.directSalesTotal)].filter(Boolean).join(' · '),
          url: v.sheetUrl,
        };
      case 'skaraSheet':
        return {
          text: [
            v.skipped ? `${v.monthLabel || ''} (already present — skipped)` : v.monthLabel,
            typeof v.channelCount === 'number' ? `${v.channelCount} channels` : '',
            typeof v.adSenseTotal === 'number' ? `AdSense ${fmtUsd(v.adSenseTotal)}` : '',
            typeof v.directSoldTotal === 'number' ? `DS ${fmtUsd(v.directSoldTotal)}` : '',
            v.unmatchedChannels?.length ? `${v.unmatchedChannels.length} missing row(s)` : '',
          ]
            .filter(Boolean)
            .join(' · '),
          url: v.sheetUrl,
        };
      case 'channelSheetSummary':
        return {
          text: `${v.updated ?? 0} updated · ${v.mirrored ?? 0} mirrored · ${(v.errors?.length ?? 0) + (v.mirrorErrors?.length ?? 0)} errors`,
        };
      default:
        return { text: '' };
    }
  };

  const fire = async (target: PayoutTarget | null, label: string) => {
    if (!window.confirm(
      `Re-run ${label} for ${pipeline.monthDisplay}? This replaces the existing month tab(s) — no duplicates are created.`
    )) return;
    try {
      await onGenerate(target ? [target] : undefined);
    } catch {
      // error surfaced by the controller
    }
  };

  return (
    <div style={styles.panel}>
      {PAYOUT_TARGETS.map(({ target, label, resultKey }) => {
        const { pill, note } = targetStatus(resultKey, target);
        const summary = targetSummary(resultKey);
        const rowBusy =
          actionBusy === `payouts:${target}` ||
          (activeTargets.has(target) && payoutStage.status === 'running');
        return (
          <div key={target} style={styles.row}>
            <div style={styles.rowMain}>
              <span style={styles.rowLabel}>
                {label} <StatusPill variant={pill} style={{ marginLeft: 6 }}>{pillLabel(pill)}</StatusPill>
              </span>
              <span style={styles.rowDetail}>
                {summary.text || note}
                {summary.text ? ` — ${note}` : ''}
                {summary.url && (
                  <>
                    {' · '}
                    <a href={summary.url} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                  </>
                )}
              </span>
            </div>
            <Button
              variant={pill === 'flagged' ? 'destructive' : 'ghost'}
              size="sm"
              disabled={!enabled || actionBusy !== null}
              onClick={() => fire(target, label)}
            >
              {rowBusy ? 'Running…' : pill === 'flagged' ? 'Retry' : 'Re-run'}
            </Button>
          </div>
        );
      })}
      <div style={styles.row}>
        <div style={styles.rowMain}>
          <span style={styles.rowLabel}>All payout sheets</span>
          <span style={styles.rowDetail}>Regenerate all eight outputs (the full stage-5 run)</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!enabled || actionBusy !== null}
          onClick={() => fire(null, 'ALL payout sheets')}
        >
          {actionBusy === 'payouts' ? 'Starting…' : 'Re-run all'}
        </Button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: 'var(--bg-surface-3)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    padding: '4px 12px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid var(--border-subtle)',
  },
  rowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  rowLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--fg-1)',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  rowDetail: {
    fontSize: 11.5,
    color: 'var(--fg-3)',
  },
  link: { color: 'var(--fg-link)' },
};

export default PipelinePayoutTargets;
